import {
  collection,
  CollectionReference,
  QuerySnapshot,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  updateDoc,
  query,
  setDoc,
  startAfter,
  Timestamp,
  where,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  deleteField
} from 'firebase/firestore'
import { firestore } from '@/modules/firebase'
import { Dayjs } from 'dayjs'
import { getRandomName } from '@/modules/https'
import { getParticipationDoc } from '@/modules/database/participations'
import { EventVisibility } from '@/modules/config'

interface ParticipantsBatchData {
  eventId: string
  members?: {
    participations: {
      user: User
      participation: Participation
    }[]
    updates: Map<string, User>
  }
  guests?: {
    participations: string[]
    updates: Map<string, Guest>
  }
}

const eventCollection = collection(firestore, 'events') as CollectionReference<
  TeamEvent & { notifyTeam?: boolean }
>
const getEventGuestsCollection = (eventId: string) => {
  return collection(
    eventCollection,
    eventId,
    'guests'
  ) as CollectionReference<Guest>
}

function getIncludedVisibilityValues(
  visibilityValue: EventVisibilityValue = 1,
  includeUnlisted = false
) {
  const values: number[] = [...Array(visibilityValue + 1).keys()]
  if (includeUnlisted) {
    values.unshift(EventVisibility.Unlisted)
  }
  return values
}

function getEventsQuery(options: EventQueryOptions) {
  let eventsQuery = query(
    eventCollection,
    where('teamId', '==', options.teamId),
    where('date', '>=', new Date()),
    where(
      'visibility',
      'in',
      getIncludedVisibilityValues(options.visibility, options.includeUnlisted)
    ),
    limit(options.limit ?? 25)
  )

  if (options.orderBy?.length) {
    options.orderBy.forEach(({ field, direction }) => {
      eventsQuery = query(eventsQuery, orderBy(field, direction))
    })
  }

  if (options.startAfter) {
    eventsQuery = query(eventsQuery, startAfter(...options.startAfter))
  }

  return eventsQuery
}

export async function createEvent(
  eventData: CreateEventPayload,
  eventDate: Dayjs
) {
  const [hours, minutes] = eventData.startTime.split(':')

  const preciseStartDate = eventDate
    .add(parseInt(hours), 'hours')
    .add(parseInt(minutes), 'minutes')

  const { policyData, ...otherData } = eventData

  const { minParticipants, maxParticipants, deadlineHours } = policyData

  const docRef = doc(eventCollection)

  await setDoc(docRef, {
    id: docRef.id,
    date: Timestamp.fromDate(preciseStartDate.toDate()),
    policy: {
      minParticipants,
      maxParticipants,
      reactionDeadline: Timestamp.fromDate(
        preciseStartDate.subtract(deadlineHours, 'hour').toDate()
      )
    },
    ...otherData,
    notifyTeam: otherData.notifyTeam ?? true
  })

  return docRef.id
}

export async function createRepeatedEvent(
  eventData: CreateEventPayload,
  eventDates: Dayjs[]
) {
  const isNotifyTeam = eventData.notifyTeam

  let eventPromises: Promise<string>[]

  if (isNotifyTeam) {
    const [first, ...rest] = eventDates

    eventPromises = [
      createEvent({ ...eventData, notifyTeam: true }, first),
      ...rest.map((date) =>
        createEvent({ ...eventData, notifyTeam: false }, date)
      )
    ]
  } else {
    eventPromises = eventDates.map((date) => createEvent(eventData, date))
  }

  return Promise.all(eventPromises)
}

export async function getEvent(eventId: string) {
  const snapshot = await getDoc(doc(eventCollection, eventId))
  const eventData = snapshot.data()
  if (eventData) {
    return eventData
  } else throw new Error('No matching document found')
}

export function getEventListener(
  eventId: string,
  onNext: (eventData: TeamEvent | undefined) => void
) {
  return onSnapshot(doc(eventCollection, eventId), (snapshot) => {
    onNext(snapshot.data())
  })
}

export async function getEvents(options: EventQueryOptions) {
  const eventsQuery = getEventsQuery(options)

  const querySnapshot = await getDocs(eventsQuery)

  return querySnapshot.docs.map((doc) => doc.data())
}

export function getEventsListener(
  options: EventQueryOptions,
  onNext: (snapshot: QuerySnapshot<TeamEvent>) => void
) {
  const eventsQuery = getEventsQuery(options)
  return onSnapshot(eventsQuery, onNext)
}

export async function updateEvent(
  eventId: string,
  eventUpdates: Partial<TeamEvent>
) {
  const eventDoc = doc(eventCollection, eventId)

  return updateDoc(eventDoc, eventUpdates)
}

export async function deleteEventSchedule(eventId: string) {
  const eventDoc = doc(eventCollection, eventId)
  return updateDoc(eventDoc, { schedule: deleteField() })
}

export async function deleteEventDocument(eventId: string) {
  return deleteDoc(doc(eventCollection, eventId))
}

export async function getPastEvents(options: EventQueryOptions) {
  let eventsQuery = query(
    eventCollection,
    where('teamId', '==', options.teamId),
    where('date', '<', new Date()),
    where(
      'visibility',
      'in',
      getIncludedVisibilityValues(options.visibility, options.includeUnlisted)
    ),
    limit(options.limit ?? 25)
  )

  if (options.orderBy?.length) {
    options.orderBy.forEach(({ field, direction }) => {
      eventsQuery = query(eventsQuery, orderBy(field, direction))
    })
  }

  if (options.startAfter) {
    eventsQuery = query(eventsQuery, startAfter(...options.startAfter))
  }

  const querySnapshot = await getDocs(eventsQuery)
  return querySnapshot.docs.map((doc) => doc.data())
}

export async function createGuest(eventId: string): Promise<Guest> {
  const guestDocRef = doc(getEventGuestsCollection(eventId))

  const result = await getRandomName({ userId: guestDocRef.id })
  const { randomName } = result.data

  const [firstName, ...lastNameSegments] = randomName.split(' ')

  return {
    id: guestDocRef.id,
    photoUrl: `https://robohash.org/${guestDocRef.id}`,
    attributes: {},
    lastName: lastNameSegments.length ? lastNameSegments.join(' ') : '',
    firstName
  }
}

export async function setGuest(eventId: string, guestData: Guest) {
  const guestDoc = doc(getEventGuestsCollection(eventId), guestData.id)
  return setDoc(guestDoc, guestData)
}

export async function updateGuest(
  eventId: string,
  guestId: string,
  updateData: Partial<Guest>
) {
  const guestDoc = doc(getEventGuestsCollection(eventId), guestId)
  return updateDoc(guestDoc, updateData)
}

export async function deleteGuest(eventId: string, guestId: string) {
  const guestDoc = doc(getEventGuestsCollection(eventId), guestId)
  return deleteDoc(guestDoc)
}

export async function getGuest(eventId: string, guestId: string) {
  const guestDoc = doc(getEventGuestsCollection(eventId), guestId)
  const guestData = (await getDoc(guestDoc)).data()
  if (guestData) {
    return guestData
  } else throw new Error('No matching document found')
}

export async function getGuests(eventId: string) {
  const guestsSnapshot = await getDocs(getEventGuestsCollection(eventId))
  return guestsSnapshot.docs.map((guestDoc) => guestDoc.data())
}

export function getGuestsListener(
  eventId: string,
  onNext: (snapshot: QuerySnapshot<Guest>) => void
) {
  const guestsQuery = query(getEventGuestsCollection(eventId))
  return onSnapshot(guestsQuery, onNext)
}

export async function batchUpdateParticipants(
  batchData: ParticipantsBatchData
) {
  const batch = writeBatch(firestore)
  const participantIds: string[] = []

  batchData.members?.participations.forEach(({ user, participation }) => {
    if (!batchData.members?.updates.has(user.id)) {
      batch.update(getParticipationDoc(participation.id), {
        state: 'canceled',
        lastUpdated: serverTimestamp()
      })
    }
    participantIds.push(user.id)
  })

  batchData.members?.updates.forEach((user) => {
    if (!participantIds.includes(user.id)) {
      const docRef = getParticipationDoc()
      batch.set(docRef, {
        id: docRef.id,
        participantId: user.id,
        lastUpdated: serverTimestamp(),
        state: 'accepted',
        eventId: batchData.eventId
      })
    }
  })

  batchData.guests?.participations.forEach((guestId) => {
    if (!batchData.guests?.updates.has(guestId)) {
      batch.delete(doc(getEventGuestsCollection(batchData.eventId), guestId))
    }

    participantIds.push(guestId)
  })

  batchData.guests?.updates.forEach((guest) => {
    if (!participantIds.includes(guest.id)) {
      batch.set(
        doc(getEventGuestsCollection(batchData.eventId), guest.id),
        guest
      )
    }
  })

  return batch.commit()
}
